import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge'
import defaultTexts from '../../../texts/texts'
import { capitalizeFirstLetter } from '../../_common/_helpers/string'
import { getTextedDate, getDifferenceInDays, getDaysInMonth, getDaysInYear } from '../_helpers/dates'
import { sortByPriority } from '../_helpers/sort'
import CustomDialog from '../../_common/CustomDialog'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import ActiveSubscriptionUpdateServiceItem from './ActiveSubscriptionUpdateServiceItem'

const ActiveSubscription = (props) => {
  const {
    id,
    title,
    services,
    fee,
    currency,
    duration,
    status,
    created_at,
    end_time,
    renewable,
    payment_method,
    packageData,
    setUpdateProps,
    handleUpdateSubscriptionDialogOpen,
    setCancelId,
    handleCancelSubscriptionDialogOpen,
    updateRenewableStatus,
  } = props
  const theme = useTheme()

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [servicesData, setServicesData] = useState([])
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  const [totalPrice, setTotalPrice] = useState(packageData && packageData.base_price ? packageData.base_price : 0)
  const [currentOptionalServices, setCurrentOptionalServices] = useState([])
  const [selectedOptionalServices, setSelectedOptionalServices] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [renewableChecked, setRenewableChecked] = useState(true)
  const [currentRenewable, setCurrentRenewable] = useState(false)
  const [differenceInDays, setDifferenceInDays] = useState(getDifferenceInDays(null, end_time))
  
  const gaugeSum = duration === 'month' ? getDaysInMonth() : getDaysInYear()

  const wrapperStyle = {
    background: theme.palette.aliceBlue,
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: '20px',
  }

  const labelStyle = {
    height: '50px',
    background: theme.palette.violetsAreBlue,
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const gaugeValueStyle = {
    width: '140px',
    fontSize: '24px',
    lineHeight: '24px',
    fontWeight: '500',
    color: theme.palette.violetsAreBlue,
    position: 'absolute',
    top: '35px',
    left: '0px',
    textAlign: 'center',
  }

  const monthlyPriceStyle = {
    color: theme.palette.violetsAreBlue,
    fontSize: '20px',
    fontWeight: '500',
  }

  const paramKeyStyle = {
    color: theme.palette.grayX11,
    fontSize: '18px',
    fontWeight: '500',
  }

  const paramValueStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
  }

  const paramBigValueStyle = {
    fontSize: '22px',
    fontWeight: '500',
  }

  const servicesLabelStyle = {
    fontSize: '20px',
    fontWeight: '500',
    color: theme.palette.oxfordBlue,
    padding: '20px 0px',
  }

  const serviceBulletStyle = {
    background: theme.palette.violetsAreBlue,
    width: '10px',
    height: '10px',
    borderRadius: '5px',
  }

  const serviceTitleStyle = {
    fontSize: '18px',
    fontWeight: '500',
    color: theme.palette.oxfordBlue,
  }

  const editButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.oxfordBlue,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.oxfordBlue, 0.8),
    },
  }

  const cancelButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.coralRed,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.coralRed, 0.8),
    },
  }

  const confirmButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const dialogServicesLabelStyle = {
    color: theme.palette.grayX11,
    fontSize: '16px',
    fontWeight: '500',
    padding: '20px 0px',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontWeight: '500',
  }

  const priceStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
  }

  const smallSaveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '16px',
    height: '30px',
    borderRadius: '20px',
    padding: '0px 20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const handleUpdateDialogOpen = () => {
    setUpdateDialogOpen(true)
  }

  const handleUpdateDialogClose = () => {
    setUpdateDialogOpen(false)
  }

  const handleUpdateSubscription = () => {
    if (isUpdate) {
      setUpdateProps({
        id: packageData && packageData.id ? packageData.id : null,
        totalPrice,
        renewable,
        selectedOptionalServices,
      })

      handleUpdateSubscriptionDialogOpen(totalPrice)
    }    

    handleUpdateDialogClose()    
  }

  const handleClickCancel = () => {
    setCancelId(id)
    handleCancelSubscriptionDialogOpen()
  }

  const handleChangeRenewableChecked = (value) => {
    setRenewableChecked(value)
  }

  useEffect(() => {
    if (services && services.length > 0) {
      setServicesData(sortByPriority(services))
    } else {
      setServicesData([])
    }
  }, [services])

  useEffect(() => {
    let current_optional_services = []
      
    services.forEach(item => {
      if (!item.is_mandatory) {
        current_optional_services.push(item.service)
      }
    })

    setCurrentRenewable(renewable)
    setCurrentOptionalServices(current_optional_services)
    setSelectedOptionalServices(current_optional_services)
  }, [services, renewable])

  useEffect(() => {
    let total_price = packageData && packageData.base_price ? parseFloat(packageData.base_price) : 0

    if (packageData && packageData.services) {
      packageData.services.forEach(item => {
        if (!item.is_mandatory && selectedOptionalServices.includes(item.service)) {
          total_price += parseFloat(item.price)
        }
      })
    }    

    setTotalPrice(total_price)
  }, [packageData, selectedOptionalServices])
  
  useEffect(() => {
    setIsUpdate((JSON.stringify(currentOptionalServices) !== JSON.stringify(selectedOptionalServices) || renewableChecked !== currentRenewable))
  }, [currentOptionalServices, selectedOptionalServices, renewableChecked, currentRenewable])

  useEffect(() => {
    setRenewableChecked(!currentRenewable ? false : true)
  }, [currentRenewable])

  useEffect(() => {
    setDifferenceInDays(getDifferenceInDays(null, end_time))
  }, [end_time])

  return (
    <>
      <Box sx={wrapperStyle}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
        >
          <Box sx={labelStyle}>{defaultTexts.activeSubscriptions}</Box>
        </Stack>

        <Box sx={{ ...servicesLabelStyle, padding: '20px' }}>{title.value}</Box>

        <Stack
          direction='column'
          alignItems='center'
          justifyContent='flex-start'
          spacing={1}
        >

          <Box sx={{ position: 'relative' }}>
            <Box sx={gaugeValueStyle}>
              <Box>{differenceInDays > 0 ? differenceInDays : 0}</Box>
              <Box sx={{ color: theme.palette.grayX11, fontSize: '20px' }}>{defaultTexts.days}</Box>
              <Box sx={{ color: theme.palette.grayX11, fontSize: '20px' }}>{defaultTexts.left}</Box>
            </Box>
            <Gauge
              width={140}
              height={140}
              value={ differenceInDays > 0 ? (differenceInDays / gaugeSum) * 100 : 0 }
              cornerRadius='50%'
              sx={{
                [`& .${gaugeClasses.valueText}`]: gaugeValueStyle,
                [`& .${gaugeClasses.valueArc}`]: {
                  fill: theme.palette.violetsAreBlue,
                },
                [`& .${gaugeClasses.referenceArc}`]: {
                  fill: theme.palette.grayX11,
                },
              }}
              text=''
            />
          </Box>

          <Box sx={monthlyPriceStyle}>{fee} {currency.toUpperCase()} / {capitalizeFirstLetter(duration)}</Box>

          <Box sx={{ width: '100%', padding: '0px 20px' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ marginBottom: '10px' }}
            >
              <Box sx={paramKeyStyle}>{defaultTexts.status}:</Box>
              <Box
                sx={{
                  ...paramBigValueStyle,
                  color: status === 'active' ? theme.palette.greenSheen : theme.palette.coralRed,
                }}
              >{capitalizeFirstLetter(status)}</Box>
            </Stack>
            
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ marginBottom: '10px' }}
            >
              <Box sx={paramKeyStyle}>{defaultTexts.subscribedAt}:</Box>
              <Box sx={paramValueStyle}>{getTextedDate(created_at)}</Box>
            </Stack>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ marginBottom: '10px' }}
            >
              <Box sx={paramKeyStyle}>{defaultTexts.autorenew}:</Box>
              <Box sx={paramValueStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-end'
                  spacing={1}
                  sx={{ marginRight: '-10px' }}
                >
                  {
                    renewableChecked !== currentRenewable &&
                    <Button
                      variant='contained'
                      disableRipple
                      disableElevation
                      disabled={false}
                      onClick={() => updateRenewableStatus(renewableChecked)}
                      sx={smallSaveButtonStyle}
                    >
                      {defaultTexts.save}
                    </Button>
                  }
                  <CustomCheckboxField
                    label=''
                    checked={renewableChecked}
                    handleChange={handleChangeRenewableChecked}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ marginBottom: '10px' }}
            >
              <Box sx={paramKeyStyle}>{defaultTexts.renewDate}:</Box>
              <Box sx={paramValueStyle}>{getTextedDate(end_time)}</Box>
            </Stack>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ marginBottom: '10px' }}
            >
              <Box sx={paramKeyStyle}>{defaultTexts.paymentMethod}:</Box>
              <Box sx={paramValueStyle}>{capitalizeFirstLetter(payment_method)}</Box>
            </Stack>
          </Box>

        </Stack>

        <Box sx={{ padding: '0px 20px' }}>
          <Box sx={servicesLabelStyle}>{defaultTexts.productsAndService}</Box>
          {
            servicesData.map((item, index) => (
              <Stack
                key={index}
                direction='row'
                alignItems='center'
                justifyContent='flex-start'
                spacing={2}
                sx={{ margin: '8px 0px' }}
              >
                <Box sx={serviceBulletStyle}></Box>
                <Box sx={serviceTitleStyle}>{item.title.value}</Box>
              </Stack>
            ))
          }
        </Box>

        {
          editPermission ?
          <Stack
            direction='row'
            justifyContent='space-between'
            sx={{ padding: '20px' }}
          >
            <Button
              variant='contained'
              disableRipple
              disableElevation
              disabled={false}
              onClick={handleUpdateDialogOpen}
              sx={editButtonStyle}
            >
              {defaultTexts.update}
            </Button>
            <Button
              variant='contained'
              disableRipple
              disableElevation
              disabled={false}
              onClick={handleClickCancel}
              sx={cancelButtonStyle}
            >
              {defaultTexts.cancel}
            </Button>
          </Stack> :
          <Box sx={{ paddingTop: '20px' }}></Box>
        }
      </Box>

      <CustomDialog
        open={updateDialogOpen}
        onClose={handleUpdateDialogClose}
        dialogTitle={{
          title: defaultTexts.updateSubscription,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          }
        }}
        actionButtons={[{
          title: defaultTexts.confirm, 
          onClick: handleUpdateSubscription,
          sx: confirmButtonStyle,
        }]}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <Box sx={priceStyle}>
          {defaultTexts.price}: {totalPrice} {currency.toUpperCase()} / {capitalizeFirstLetter(duration)}
        </Box>

        <Box sx={dialogServicesLabelStyle}>{defaultTexts.services}:</Box>

        {
          (packageData && packageData.services) &&
          <>
            {
              packageData.services.map((item, index) => (
                <ActiveSubscriptionUpdateServiceItem
                  key={index}
                  {...item}
                  selectedOptionalServices={selectedOptionalServices}
                  setSelectedOptionalServices={setSelectedOptionalServices}
                />
              ))
            }
          </>
        }
      </CustomDialog>
    </>   
  )
}

export default ActiveSubscription